import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Reveal the app now that React has committed its first paint. The inline
// splash in index.html was covering everything until this moment so the
// user never sees a stale UI briefly paint on cold load. If a SW update
// is in progress (see below) we KEEP the splash up until the reload.
const revealApp = () => {
  try {
    if (window.__fw_sw_update_pending) return; // hold splash for reload
    if (window.__fw_splash_timer) { clearTimeout(window.__fw_splash_timer); window.__fw_splash_timer = null; }
    document.documentElement.classList.add("__fw_app_ready");
  } catch { /* ignore */ }
};
// requestAnimationFrame after render → guarantees React committed to DOM.
if (typeof window !== "undefined") {
  requestAnimationFrame(() => requestAnimationFrame(revealApp));
}

// Register service worker for PWA (offline shell + install prompt).
//
// Update flow: when a new SW version is installed, we automatically tell it
// to skipWaiting and reload the page once it takes control. This is how the
// watch / phone pick up new builds without a manual hard refresh — the next
// time the user opens the app, they get the latest code.
//
// To avoid the "old home page flashes for a few seconds, then the new home
// page overlays it" bug we (1) mark an update as pending the moment a new
// SW is detected — the inline splash in index.html then stays visible
// until reload, so the user never sees the stale UI paint — and (2) call
// reg.update() immediately so the check happens fast.
const markUpdatePending = () => {
  try {
    window.__fw_sw_update_pending = true;
    // Force the splash back on if React already revealed the app.
    document.documentElement.classList.remove("__fw_app_ready");
  } catch { /* ignore */ }
};

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        if (!reg) return;
        const promote = (worker) => {
          if (!worker) return;
          markUpdatePending();
          worker.addEventListener("statechange", () => {
            if (worker.state === "installed" && navigator.serviceWorker.controller) {
              // A new version is ready — activate immediately.
              worker.postMessage({ type: "SKIP_WAITING" });
            }
          });
        };
        if (reg.waiting) promote(reg.waiting);
        if (reg.installing) promote(reg.installing);
        reg.addEventListener("updatefound", () => promote(reg.installing));
        // Force an immediate update check so we don't rely on the browser's
        // ~24h heuristic — this is what shrinks the flash window on
        // production.
        try { reg.update(); } catch { /* ignore */ }
        // Reload the page exactly once when the new SW takes over so users
        // immediately see the new shell without a manual refresh.
        let refreshing = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (refreshing) return;
          refreshing = true;
          window.location.reload();
        });
      })
      .catch(() => {
        /* SW registration is best-effort; silently ignore on unsupported envs. */
      });
  });
}
